import { TILE_KIND } from './type';

// Para el MVP, solo matcheamos SIGN (los 12 signos).
// Más adelante, ELEMENT y PLANET se comportan como especiales.

function signKeyAt(board, r, c) {
  const t = board.grid[r]?.[c];
  if (!t) return null;
  if (t.kind !== TILE_KIND.SIGN) return null;
  return t.key;
}

function computeGroupStats(cells, ctx, rows, cols) {
  // Build a quick lookup for membership
  const keyOf = (p) => `${p.r},${p.c}`;
  const inSet = new Set((cells || []).map(keyOf));

  // Group cells per row/col
  const byRow = new Map();
  const byCol = new Map();

  for (const { r, c } of cells) {
    if (!byRow.has(r)) byRow.set(r, []);
    if (!byCol.has(c)) byCol.set(c, []);
    byRow.get(r).push(c);
    byCol.get(c).push(r);
  }

  // Helper: longest contiguous run given a sorted number array
  const longestContig = (arr) => {
    if (!arr || arr.length === 0) return 0;
    const a = arr.slice().sort((x, y) => x - y);
    let best = 1;
    let cur = 1;
    for (let i = 1; i < a.length; i++) {
      if (a[i] === a[i - 1] + 1) {
        cur += 1;
      } else if (a[i] !== a[i - 1]) {
        cur = 1;
      }
      if (cur > best) best = cur;
    }
    return best;
  };

  // Longest contiguous horizontal/vertical runs inside the connected group
  let maxRowRun = 0;
  let maxColRun = 0;

  for (const colsInRow of byRow.values()) {
    maxRowRun = Math.max(maxRowRun, longestContig(colsInRow));
  }
  for (const rowsInCol of byCol.values()) {
    maxColRun = Math.max(maxColRun, longestContig(rowsInCol));
  }

  const lenLine = Math.max(maxRowRun, maxColRun);

  // Candy Crush-ish power-up rules (STRICT):
  // - 5 in a straight line (horizontal OR vertical) => STAR
  // - T/L shape (>=5 cells with an actual intersection of runs >=3) => BOMB
  // - 4 in a straight line (exact) => STRIPE
  // - <4 => NO powerup

  let powerup = { type: null, orient: null, len: lenLine };

  if (!Array.isArray(cells) || cells.length < 4) {
    return { size: Array.isArray(cells) ? cells.length : 0, maxRowRun, maxColRun, powerup };
  }

  // Detect straight-line group (all same row OR all same col)
  let allSameRow = true;
  let allSameCol = true;
  const r0 = cells[0]?.r;
  const c0 = cells[0]?.c;
  for (const p of cells) {
    if (p.r !== r0) allSameRow = false;
    if (p.c !== c0) allSameCol = false;
  }
  const isStraightLine = allSameRow || allSameCol;

  // Detect true T/L: there exists at least one cell that belongs to BOTH
  // a horizontal contiguous run >=3 AND a vertical contiguous run >=3.
  const hasRunAt = (r, c, axis) => {
    if (!inSet.has(`${r},${c}`)) return false;
    let count = 1;

    if (axis === 'h') {
      // left
      for (let cc = c - 1; cc >= 0; cc--) {
        if (!inSet.has(`${r},${cc}`)) break;
        count += 1;
      }
      // right
      const rightBound = Number.isInteger(cols) ? cols : 0;
      for (let cc = c + 1; cc < rightBound; cc++) {
        if (!inSet.has(`${r},${cc}`)) break;
        count += 1;
      }
    } else {
      // up
      for (let rr = r - 1; rr >= 0; rr--) {
        if (!inSet.has(`${rr},${c}`)) break;
        count += 1;
      }
      // down
      const downBound = Number.isInteger(rows) ? rows : 0;
      for (let rr = r + 1; rr < downBound; rr++) {
        if (!inSet.has(`${rr},${c}`)) break;
        count += 1;
      }
    }

    return count >= 3;
  };

  let isTLShape = false;
  if (cells.length >= 5) {
    for (const p of cells) {
      const hOk = hasRunAt(p.r, p.c, 'h');
      if (!hOk) continue;
      const vOk = hasRunAt(p.r, p.c, 'v');
      if (vOk) {
        isTLShape = true;
        break;
      }
    }
  }

  // Decide powerup
  if (isTLShape) {
    powerup = { type: 'bomb', orient: null, len: lenLine };
  } else if (isStraightLine && lenLine >= 5) {
    powerup = { type: 'star', orient: null, len: lenLine };
  } else if (isStraightLine && lenLine === 4) {
    let orient = allSameRow ? 'h' : 'v';

    // Si el swap destination participa, respetar el eje del swap
    const sr = ctx?.swapTo?.r;
    const sc = ctx?.swapTo?.c;
    const hasSwapTo =
      Number.isInteger(sr) &&
      Number.isInteger(sc) &&
      cells.some((p) => p.r === sr && p.c === sc);

    if (hasSwapTo && (ctx?.swapAxis === 'x' || ctx?.swapAxis === 'y')) {
      orient = ctx.swapAxis === 'y' ? 'v' : 'h';
    }

    powerup = { type: 'stripe', orient, len: lenLine };
  }

  return { size: cells.length, maxRowRun, maxColRun, powerup };
}

// Devuelve un array de grupos (componentes conectados de celdas marcadas):
// [{ key: 'aries', cells: [{r,c}, ...], size, maxRowRun, maxColRun, powerup }, ...]
// - size: cantidad total de celdas en el grupo
// - maxRowRun: mayor cantidad de celdas del grupo en una misma fila
// - maxColRun: mayor cantidad de celdas del grupo en una misma columna
// - powerup: info para resolve (Candy Crush style) basada en runs 4/5:
//   { type: 'stripe'|'star'|'bomb'|null, orient: 'h'|'v'|null, len: number }
export function findMatches(board, ctx = null) {
  const rows = board.rows;
  const cols = board.cols;

  // marked[r][c] = true si esa celda forma parte de algún match (h o v)
  const marked = Array.from({ length: rows }, () => Array.from({ length: cols }, () => false));

  // =====================
  // 1) Barrido horizontal
  // =====================
  for (let r = 0; r < rows; r++) {
    let runKey = null;
    let runStart = 0;
    let runLen = 0;

    for (let c = 0; c < cols; c++) {
      const k = signKeyAt(board, r, c);

      if (k && k === runKey) {
        runLen += 1;
      } else {
        if (runKey && runLen >= 3) {
          for (let cc = runStart; cc < runStart + runLen; cc++) marked[r][cc] = true;
        }
        runKey = k;
        runStart = c;
        runLen = k ? 1 : 0;
      }
    }

    // flush run
    if (runKey && runLen >= 3) {
      for (let cc = runStart; cc < runStart + runLen; cc++) marked[r][cc] = true;
    }
  }

  // =====================
  // 2) Barrido vertical
  // =====================
  for (let c = 0; c < cols; c++) {
    let runKey = null;
    let runStart = 0;
    let runLen = 0;

    for (let r = 0; r < rows; r++) {
      const k = signKeyAt(board, r, c);

      if (k && k === runKey) {
        runLen += 1;
      } else {
        if (runKey && runLen >= 3) {
          for (let rr = runStart; rr < runStart + runLen; rr++) marked[rr][c] = true;
        }
        runKey = k;
        runStart = r;
        runLen = k ? 1 : 0;
      }
    }

    // flush run
    if (runKey && runLen >= 3) {
      for (let rr = runStart; rr < runStart + runLen; rr++) marked[rr][c] = true;
    }
  }

  // =====================
  // 3) Agrupar en componentes conectados (4-dir)
  //    y además mantener consistencia por key.
  // =====================
  const seen = Array.from({ length: rows }, () => Array.from({ length: cols }, () => false));
  const groups = [];

  const dirs = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ];

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (!marked[r][c] || seen[r][c]) continue;

      const k0 = signKeyAt(board, r, c);
      if (!k0) {
        seen[r][c] = true;
        continue;
      }

      const stack = [[r, c]];
      const cells = [];
      seen[r][c] = true;

      while (stack.length) {
        const [rr, cc] = stack.pop();
        cells.push({ r: rr, c: cc });

        for (const [dr, dc] of dirs) {
          const nr = rr + dr;
          const nc = cc + dc;
          if (nr < 0 || nc < 0 || nr >= rows || nc >= cols) continue;
          if (seen[nr][nc]) continue;
          if (!marked[nr][nc]) continue;
          if (signKeyAt(board, nr, nc) !== k0) continue;

          seen[nr][nc] = true;
          stack.push([nr, nc]);
        }
      }

      // Candy Crush: ensure swap destination becomes the anchor (last cell),
      // because resolve.js currently uses the last cell as anchor.
      if (ctx?.swapTo && Number.isInteger(ctx.swapTo.r) && Number.isInteger(ctx.swapTo.c)) {
        const idx = cells.findIndex((p) => p.r === ctx.swapTo.r && p.c === ctx.swapTo.c);
        if (idx >= 0 && idx !== cells.length - 1) {
          const [picked] = cells.splice(idx, 1);
          cells.push(picked);
        }
      }

      const stats = computeGroupStats(cells, ctx, rows, cols);
      groups.push({ key: k0, cells, ...stats });
    }
  }

  return groups;
}